<template>
  <button
    :id="tabId(tabs.base, value)"
    type="button"
    role="tab"
    :class="['ui-tab', `ui-tab--${tabs.variant.value}`, { 'is-active': active }]"
    :aria-selected="active"
    :aria-controls="panelId(tabs.base, value)"
    :tabindex="active ? 0 : -1"
    :disabled="disabled"
    :data-value="value"
    @click="tabs.select(value)"
  >
    <slot />
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { panelId, tabId, useTabs } from './context'

const props = withDefaults(defineProps<{ value: string; disabled?: boolean }>(), {
  disabled: false,
})

const tabs = useTabs('TabsTrigger')
const active = computed(() => tabs.current.value === props.value)
</script>

<style scoped>
.ui-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: none;
  font-family: inherit;
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--color-text-muted);
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color var(--transition-base) ease,
    color var(--transition-base) ease;
}

.ui-tab:focus-visible {
  outline: none;
  box-shadow: var(--shadow-focus);
}

.ui-tab:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ui-tab--segmented {
  padding: 5px 13px;
  border-radius: var(--radius-pill);
}

.ui-tab--segmented:hover:not(:disabled, .is-active) {
  color: var(--color-text);
}

.ui-tab--segmented.is-active {
  background: var(--color-primary);
  color: var(--color-on-primary);
}

.ui-tab--underline {
  margin-bottom: -1px;
  padding: 8px 0;
  border-bottom: 2px solid transparent;
}

.ui-tab--underline:hover:not(:disabled, .is-active) {
  color: var(--color-text);
}

.ui-tab--underline.is-active {
  border-bottom-color: var(--color-primary);
  color: var(--color-text);
}
</style>
